import {
  AuditLogWithPagination,
  AuditLogWebService,
  RestfulAuditLogWebService
} from 'ws/AuditLogWebService';
import { Pageable } from 'ws/Pageable';
import { AUDITLOG_COLUMN } from './AuditLog';

export interface AuditLogManager {
  getOrderAndL1ObjectAuditLogs(
    orderId: number,
    pageable: Pageable,
    searchString: string
  ): Promise<AuditLogWithPagination>;
  getOrderAndL1ObjectAuditLogsFields(
    orderId: number,
    l1ObjectIds: (number | string)[]
  ): Promise<string[]>;
  getCampaignAuditLog(
    pageable: Pageable,
    searchString: string
  ): Promise<AuditLogWithPagination>;
  getCampaignAuditLogFields(
    campaignIds: (number | string)[]
  ): Promise<string[]>;
  getAgencyAuditLog(
    agencyId: number,
    pageable: Pageable,
    searchString: string
  ): Promise<AuditLogWithPagination>;
  getAgencyAuditLogFields(agencyId: number): Promise<string[]>;
  /** ECOPD-5701：單一投遞渠道的操作歷程。沒有對應的 msgKeys（不做欄位篩選）。 */
  getChannelGroupAuditLog(
    channelGroupId: number,
    pageable: Pageable
  ): Promise<AuditLogWithPagination>;
}

const columnSortFieldMap = {
  [AUDITLOG_COLUMN.DATE]: 'id',
  [AUDITLOG_COLUMN.SOURCE_INFO]: 'srcID',
  [AUDITLOG_COLUMN.EMAIL]: 'userId',
  [AUDITLOG_COLUMN.EVENT]: 'funcType'
};

export class DefaultAuditLogManager implements AuditLogManager {
  constructor(
    private webService: AuditLogWebService = new RestfulAuditLogWebService()
  ) {}

  replaceColumnNameToSortField(pageable: Pageable): Pageable {
    return {
      ...pageable,
      sort: pageable.sort ? columnSortFieldMap[pageable.sort] : undefined
    };
  }

  async getOrderAndL1ObjectAuditLogs(
    orderId: number,
    pageable: Pageable,
    searchString: string
  ): Promise<AuditLogWithPagination> {
    const auditLogWithPagination =
      await this.webService.getOrderAndL1ObjectAuditLogs(
        orderId,
        this.replaceColumnNameToSortField(pageable),
        searchString
      );
    return auditLogWithPagination;
  }

  async getOrderAndL1ObjectAuditLogsFields(
    orderId: number,
    l1ObjectIds: (number | string)[]
  ): Promise<string[]> {
    return this.webService.getOrderAndL1ObjectAuditLogFields(
      orderId,
      l1ObjectIds
    );
  }

  async getCampaignAuditLog(
    pageable: Pageable,
    searchString: string
  ): Promise<AuditLogWithPagination> {
    const auditLogWithPagination = await this.webService.getCampaignAuditLog(
      this.replaceColumnNameToSortField(pageable),
      searchString
    );
    return auditLogWithPagination;
  }

  async getCampaignAuditLogFields(
    campaignIds: (number | string)[]
  ): Promise<string[]> {
    return this.webService.getCampaignAuditLogFields(campaignIds);
  }

  async getAgencyAuditLog(
    agencyId: number,
    pageable: Pageable,
    searchString: string
  ): Promise<AuditLogWithPagination> {
    const auditLogWithPagination = await this.webService.getAgencyAuditLog(
      agencyId,
      this.replaceColumnNameToSortField(pageable),
      searchString
    );
    return auditLogWithPagination;
  }

  async getAgencyAuditLogFields(agencyId: number): Promise<string[]> {
    return this.webService.getAgencyAuditLogFields(agencyId);
  }

  /**
   * ECOPD-5701：⚠️ **刻意不套** `replaceColumnNameToSortField`。那張 map 會把
   * `ctime` 翻成 `id`，而它同時服務 order / campaign / agency 三條線，改它是破壞性變更。
   * 渠道要的是真正的 `ctime` 排序（同渠道所有 log 的 `srcID` 相同，分頁會跳筆）。
   * 代價是這條線不支援點其他表頭排序，目前也沒這個需求。
   */
  async getChannelGroupAuditLog(
    channelGroupId: number,
    pageable: Pageable
  ): Promise<AuditLogWithPagination> {
    return this.webService.getChannelGroupAuditLog(channelGroupId, pageable);
  }
}
